import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import UserProfile from './components/UserProfile';
import FamilyMemberForm from './components/FamilyMemberForm';
import EditMemberForm from './components/EditMemberForm';
import VisualFamilyTree from './components/VisualFamilyTree';
import ApiService from './services/api';
import './App.css';

// Main App Component (wrapped in AuthProvider)
function AppContent() {
  const { currentUser, userToken, logout, loading: authLoading } = useAuth();
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const [successMessage, setSuccessMessage] = useState('');

  // Set API token when user authentication changes
  useEffect(() => {
    if (userToken) {
      ApiService.setToken(userToken);
    } else {
      ApiService.setToken(null);
    }
  }, [userToken]);

  // Load family members when user is authenticated
  useEffect(() => {
    const loadFamilyMembers = async () => {
      if (!currentUser || !userToken) {
        setFamilyMembers([]);
        return;
      }

      setLoading(true);
      setConnectionStatus('connecting');
      
      try {
        const response = await ApiService.getAllMembers();
        if (response.success) {
          setFamilyMembers(response.members);
          setConnectionStatus('online');
          console.log(`✅ Loaded ${response.members.length} family members from database`);
        }
      } catch (error) {
        console.error('Failed to load family members:', error);
        setConnectionStatus('offline');
        
        // Fallback to localStorage for offline support
        const savedMembers = localStorage.getItem(`imvelaphi_family_members_${currentUser.uid}`);
        if (savedMembers) {
          try {
            setFamilyMembers(JSON.parse(savedMembers));
            console.log('📱 Loaded family members from local storage');
          } catch (parseError) {
            console.error('Error parsing local storage data:', parseError);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadFamilyMembers();
  }, [currentUser, userToken]);

  // Save to localStorage as backup (user-specific)
  useEffect(() => {
    if (currentUser && familyMembers.length > 0) {
      localStorage.setItem(`imvelaphi_family_members_${currentUser.uid}`, JSON.stringify(familyMembers));
    }
  }, [familyMembers, currentUser]);

  // Auto-hide success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleAddMember = async (newMember) => {
    if (!currentUser) {
      setShowAuthModal(true);
      setAuthMode('login');
      return;
    }

    setLoading(true);
    
    try {
      const response = await ApiService.addMember(newMember);
      if (response.success) {
        setFamilyMembers(prev => [...prev, response.member]);
        setShowAddForm(false);
        setConnectionStatus('online');
        setSuccessMessage(`${response.member.fullName} has been added to your family tree! 🌳`);
        console.log(`✅ Added family member: ${response.member.fullName}`);
      }
    } catch (error) {
      console.error('Failed to add family member:', error);
      setConnectionStatus('offline');
      
      // Fallback to local storage
      const memberWithId = { ...newMember, id: Date.now(), createdAt: new Date().toISOString() };
      setFamilyMembers(prev => [...prev, memberWithId]);
      setShowAddForm(false);
      setSuccessMessage(`${newMember.fullName} added to your family tree! 🌳 (Saved locally)`);
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Proper edit function that opens a form instead of using prompt
  const handleEditMember = async (member) => {
    if (!currentUser) {
      setShowAuthModal(true);
      setAuthMode('login');
      return;
    }

    // Set the member to edit and show the edit form
    setEditingMember(member);
    setShowEditForm(true);
  };

  // FIXED: Handle the actual member update from the edit form
  const handleUpdateMember = async (updatedMemberData) => {
    if (!editingMember || !currentUser) return;

    setLoading(true);
    
    try {
      const memberId = editingMember._id || editingMember.id;
      
      if (connectionStatus === 'online') {
        const response = await ApiService.updateMember(memberId, updatedMemberData);
        if (response.success) {
          // Update local state with server response
          setFamilyMembers(prev => 
            prev.map(m => 
              (m._id === memberId || m.id === memberId) ? response.member : m
            )
          );
          setSuccessMessage(`${response.member.fullName} has been updated! ✏️`);
        }
      } else {
        // Offline update
        const updatedMember = { ...editingMember, ...updatedMemberData, updatedAt: new Date().toISOString() };
        setFamilyMembers(prev => 
          prev.map(m => 
            (m._id === memberId || m.id === memberId) ? updatedMember : m
          )
        );
        setSuccessMessage(`${updatedMember.fullName} updated locally! ✏️`);
      }
      
      setShowEditForm(false);
      setEditingMember(null);
    } catch (error) {
      console.error('Failed to update member:', error);
      // Still update locally on error
      const memberId = editingMember._id || editingMember.id;
      const updatedMember = { ...editingMember, ...updatedMemberData, updatedAt: new Date().toISOString() };
      setFamilyMembers(prev => 
        prev.map(m => 
          (m._id === memberId || m.id === memberId) ? updatedMember : m
        )
      );
      setSuccessMessage(`${updatedMember.fullName} updated locally! ✏️`);
      setShowEditForm(false);
      setEditingMember(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!currentUser) {
      setShowAuthModal(true);
      setAuthMode('login');
      return;
    }

    const memberToDelete = familyMembers.find(m => m._id === memberId || m.id === memberId);
    
    if (!window.confirm(`Are you sure you want to remove ${memberToDelete?.fullName} from the family tree?`)) {
      return;
    }

    setLoading(true);
    
    try {
      if (connectionStatus === 'online') {
        await ApiService.deleteMember(memberId);
      }
      
      setFamilyMembers(prev => prev.filter(member => member._id !== memberId && member.id !== memberId));
      setSuccessMessage(`${memberToDelete.fullName} has been removed from the family tree.`);
      console.log(`✅ Deleted family member: ${memberToDelete.fullName}`);
    } catch (error) {
      console.error('Failed to delete family member:', error);
      // Still delete locally
      setFamilyMembers(prev => prev.filter(member => member._id !== memberId && member.id !== memberId));
      setSuccessMessage(`${memberToDelete.fullName} removed locally.`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setFamilyMembers([]);
      setSuccessMessage('Successfully logged out! 👋');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getConnectionStatusDisplay = () => {
    if (!currentUser) {
      return { icon: '🔓', text: 'Not logged in - limited features', color: '#ef4444' };
    }

    switch (connectionStatus) {
      case 'online':
        return { icon: '🌐', text: 'Connected to cloud database', color: '#16a34a' };
      case 'offline':
        return { icon: '📱', text: 'Working offline - data saved locally', color: '#ea580c' };
      case 'connecting':
        return { icon: '🔄', text: 'Connecting...', color: '#d97706' };
      default:
        return { icon: '🌐', text: 'Connected', color: '#16a34a' };
    }
  };

  const connectionDisplay = getConnectionStatusDisplay();

  if (authLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#fef3c7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'spin 2s linear infinite' }}>⏳</div>
          <p style={{ color: '#92400e', fontSize: '18px' }}>Loading Imvelaphi...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fef3c7' }}>
      {/* Header */}
      <header style={{ 
        backgroundColor: 'white', 
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', 
        borderBottom: '4px solid #fbbf24' 
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '24px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ 
              fontSize: '32px', 
              fontWeight: 'bold', 
              color: '#92400e', 
              marginBottom: '4px' 
            }}>
              🧬 Imvelaphi
            </h1>
            <p style={{ color: '#d97706', fontStyle: 'italic' }}>
              Discover your roots, honor your journey
            </p>
            <p style={{ 
              fontSize: '12px', 
              color: connectionDisplay.color, 
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span>{connectionDisplay.icon}</span>
              {connectionDisplay.text}
            </p>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {currentUser ? (
              <>
                <button
                  onClick={() => setShowUserProfile(true)}
                  style={{
                    backgroundColor: '#6b7280',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>👤</span>
                  <span>{currentUser.displayName || 'Profile'}</span>
                </button>
                
                <button
                  onClick={() => setShowAddForm(true)}
                  disabled={loading}
                  style={{
                    backgroundColor: loading ? '#9ca3af' : '#d97706',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>+</span>
                  <span>{loading ? 'Adding...' : 'Add Family Member'}</span>
                </button>

                <button
                  onClick={handleLogout}
                  style={{
                    backgroundColor: '#ef4444',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setShowAuthModal(true);
                  setAuthMode('login');
                }}
                style={{
                  backgroundColor: '#d97706',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>🔐</span>
                <span>Sign In</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Success Message */}
      {successMessage && (
        <div style={{
          backgroundColor: '#dcfce7',
          border: '1px solid #bbf7d0',
          color: '#166534',
          padding: '12px 16px',
          margin: '16px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          animation: 'slideIn 0.3s ease'
        }}>
          <span>✅</span>
          {successMessage}
        </div>
      )}

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 16px' }}>
        {/* Welcome Message */}
        {!currentUser && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            marginBottom: '32px',
            textAlign: 'center',
            borderLeft: '4px solid #fbbf24',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              Sawubona! Welcome to Imvelaphi 👋
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              Sign in to start building your family tree and preserving your heritage for future generations.
            </p>
            <button
              onClick={() => {
                setShowAuthModal(true);
                setAuthMode('signup');
              }}
              style={{
                backgroundColor: '#d97706',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Get Started - Create Account
            </button>
          </div>
        )}

        {/* Family Tree for authenticated users */}
        {currentUser && (
          <>
            {familyMembers.length === 0 && !loading && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '32px',
                marginBottom: '32px',
                textAlign: 'center',
                borderLeft: '4px solid #fbbf24',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
                  Welcome back, {currentUser.displayName}! 👋
                </h2>
                <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                  Start your journey by adding the first member of your family tree.
                </p>
              </div>
            )}

            {loading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'spin 2s linear infinite' }}>⏳</div>
                <p style={{ color: '#6b7280' }}>Loading your family tree...</p>
              </div>
            )}

            <VisualFamilyTree 
              familyMembers={familyMembers}
              onEditMember={handleEditMember}
              onDeleteMember={handleDeleteMember}
              loading={loading}
            />

            {/* Family Stats */}
            {familyMembers.length > 0 && (
              <div style={{
                marginTop: '40px',
                padding: '24px',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#92400e', marginBottom: '16px', textAlign: 'center' }}>
                  📊 Your Family Tree Statistics
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d97706' }}>{familyMembers.length}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Members</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d97706' }}>
                      {new Set(familyMembers.map(m => m.isibongo).filter(Boolean)).size}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Clan Names</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d97706' }}>
                      {new Set(familyMembers.map(m => m.relationship)).size}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Relationships</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d97706' }}>
                      {familyMembers.filter(m => m.birthDate).length}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>With Dates</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer style={{ backgroundColor: '#92400e', color: 'white', padding: '32px 16px', marginTop: '64px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>Imvelaphi</h3>
          <p style={{ color: '#fbbf24', marginBottom: '16px' }}>
            Preserving African family heritage for future generations
          </p>
          <div style={{ fontSize: '14px', color: '#fcd34d' }}>
            Built with ❤️ for African families | Version 3.0.0 | 
            {currentUser ? ` Signed in as ${currentUser.displayName}` : ' Guest Mode'} |
            {connectionStatus === 'online' ? ' Cloud Connected ☁️' : ' Working Offline 📱'}
          </div>
        </div>
      </footer>

      {/* Modals */}
      {showAddForm && (
        <FamilyMemberForm
          onAddMember={handleAddMember}
          onClose={() => setShowAddForm(false)}
          loading={loading}
        />
      )}

      {showEditForm && editingMember && (
        <EditMemberForm
          member={editingMember}
          onUpdateMember={handleUpdateMember}
          onClose={() => {
            setShowEditForm(false);
            setEditingMember(null);
          }}
          loading={loading}
        />
      )}

      {showAuthModal && (
        authMode === 'login' ? (
          <Login
            onSwitchToSignup={() => setAuthMode('signup')}
            onClose={() => setShowAuthModal(false)}
          />
        ) : (
          <Signup
            onSwitchToLogin={() => setAuthMode('login')}
            onClose={() => setShowAuthModal(false)}
          />
        )
      )}

      {showUserProfile && currentUser && (
        <UserProfile
          user={currentUser}
          onClose={() => setShowUserProfile(false)}
        />
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Main App with AuthProvider wrapper
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;